import { useDatabus } from "@/redux/hooks";
import * as ECharts from "echarts";
import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import useResizeObserver from "use-resize-observer";
import ChartService from "../../../services/ChartService";
import DataBus from "../../../services/DataBus";
import { DataBusSubKeys } from "../../../utils/Constants";
import StringUtils from "../../../utils/StringUtils";

interface ChartResizeEvent {
  identifier: string;
}

export type ChartComponentProps = {
  ignoreDelay?: boolean;
  options: any; // | EChartsResponsiveOption;
  onChartClick?: (params: any, chart: any) => void;
  onOptionsChange?: (chart: any) => void;
  onLegendChange?: (chart: any, param: any) => void;
  onMounted?: (identifier: string, chart: any) => void;
  clearOnChange?: boolean;
  renderer: "svg" | "canvas";
  loading?: boolean;
  err?: any;
  syncZoomID?: string;
  map?: any;
  identifier?: string;
};

const CHART_CREATION_DELAY = 600;

const ChartComponent = (props: ChartComponentProps) => {
  const [identifierBackup] = useState(`chart-${StringUtils.random(30)}`);
  const chartRef = useRef<any>(null);
  const mountedRef = useRef<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const { width, height } = useResizeObserver({
    ref: containerRef,
  });
  const usedIdentifier = props.identifier || identifierBackup;
  useEffect(() => {
    if (width || height) {
      console.log("call resize chart", width, height, usedIdentifier);
      DataBus.emit(DataBusSubKeys.CHART_RESIZE, {
        identifier: usedIdentifier,
      });
    }
  }, [width, height, usedIdentifier, chartRef.current]);
  useDatabus(
    DataBusSubKeys.CHART_RESIZE,
    ({ identifier }) => {
      console.log(
        "receive resize chart",
        identifier,
        usedIdentifier,
        identifier === usedIdentifier
      );
      if (usedIdentifier === identifier) {
        chartRef.current?.resize({
          silent: true,
        });
      }
    },
    [usedIdentifier, chartRef.current]
  );
  useEffect(() => {
    mountedRef.current = true;

    if (props.map) {
      ECharts.registerMap(usedIdentifier, props.map);
    }

    chartRef.current = ECharts.init(
      document.querySelector("#" + usedIdentifier),
      "default",
      {
        renderer: props.renderer,
      }
    );

    if (props.onChartClick) {
      chartRef.current.on("click", (params: any) =>
        props.onChartClick?.(params, chartRef.current)
      );
    }

    if (props.onMounted) {
      props.onMounted(usedIdentifier, chartRef.current);
    }

    setTimeout(() => {
      chartRef.current.resize({
        silent: true,
      });
    });

    chartRef.current.showLoading("default", {
      text: "",
    });

    if (props.onLegendChange) {
      chartRef.current.on("legendselectchanged", (param) =>
        props.onLegendChange(chartRef.current, param)
      );
    }

    setTimeout(
      () => {
        if (mountedRef.current && props.options) {
          chartRef.current.setOption(props.options);
          if (props.loading === undefined || props.loading === false) {
            chartRef.current.hideLoading();
          }
        }
      },
      props.ignoreDelay ? 0 : CHART_CREATION_DELAY
    );

    if (props.syncZoomID) {
      ChartService.registerSyncDatazoom(props.syncZoomID, chartRef.current);
    }

    return () => {
      mountedRef.current = false;

      if (props.syncZoomID) {
        ChartService.unregisterSyncDatazoom(props.syncZoomID, chartRef.current);
      }
      if (chartRef.current) {
        chartRef.current.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (props.loading) {
      chartRef.current?.showLoading("default", { text: "" });
    } else {
      chartRef.current?.hideLoading();
    }
  }, [props.loading]);

  useEffect(() => {
    if (props.options) {
      setTimeout(() => {
        if (props.clearOnChange) {
          chartRef.current?.clear();
        }
        chartRef.current?.setOption(props.options, {
          ...(props.clearOnChange ? { replaceMerge: "series" } : {}),
        });
        if (props.onOptionsChange) {
          props.onOptionsChange(chartRef.current);
        }
      });
    }
  }, [props.options, props.clearOnChange]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", position: "relative" }}
      className="ChartComponent"
      onMouseOut={() => {
        // fix bug where tooltips are not hidden when mouse moved out too fast
        chartRef.current?.dispatchAction({
          type: "hideTip",
        });
      }}
    >
      <div
        id={usedIdentifier}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      ></div>
    </div>
  );
};

export default connect()(ChartComponent) as any;
